"use client";

import { motion } from "framer-motion";
import { CareerItem } from "@/data/types";
import { useData } from "@/i18n/LangContext";

interface CareerTimelineProps {
  title: string;
  timeline: CareerItem[];
}

export function CareerTimeline({ title, timeline }: CareerTimelineProps) {
  const { ui } = useData();
  return (
    <motion.div
      className="mt-16"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 1.4 }}
    >
      <motion.h2
        className="text-2xl font-bold text-[#88ff88] mb-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.5 }}
      >
        {title}
      </motion.h2>

      <motion.div
        className="relative border-l-2 border-[#88ff88]/30 pl-8 pb-8 ml-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 1.6 }}
      >
        {timeline.map((item, index) => (
          <motion.div
            key={index}
            className="mb-10 relative"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.6 + (index * 0.1) }}
          >
            <span className="absolute w-4 h-4 bg-[#0C2019] border-2 border-[#88ff88] rounded-full -left-[2.25rem] top-1"></span>
            <p className="text-[#88ff88] font-bold">{item.year}</p>
            <h3 className="text-xl font-semibold mt-1">{item.title}</h3>
            <p className="text-white/80 font-medium">{item.company}</p>
            <div className="flex flex-wrap gap-2 items-center mt-1">
              <p className="text-white/60 text-sm">{item.location}</p>
              {item.companyType && (
                <span className="text-white/50 text-xs px-2 py-0.5 bg-[#88ff88]/10 rounded-full">
                  {item.companyType}
                </span>
              )}
            </div>
            <p className="mt-2 text-content">{item.description}</p>
            
            {item.tasks && item.tasks.length > 0 && (
              <div className="mt-3">
                <p className="text-[#88ff88]/80 text-sm font-medium mb-1">{ui.cv.tasks}</p>
                <ul className="list-disc list-outside text-white/80 text-sm pl-4 space-y-0.5">
                  {item.tasks.map((task, taskIndex) => (
                    <li key={taskIndex} className="text-sm">{task}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {item.clients && item.clients.length > 0 && (
              <div className="mt-3">
                <p className="text-[#88ff88]/80 text-sm font-medium mb-1">{ui.cv.clients}</p>
                <div className="flex flex-wrap gap-1">
                  {item.clients.map((client, clientIndex) => (
                    <span 
                      key={clientIndex} 
                      className="text-sm px-2 py-0.5 bg-[#88ff88]/10 rounded-full text-white/80"
                    >
                      {client}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
}